/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  NonNullableFormBuilder,
  ValidatorFn,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { Validators } from 'ngx-editor';
import { AnyoComponent } from 'projects/app-core/src/app/models/AnyoComponent';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';
import {
  AnyoS3Buckets,
  FileUtilsService,
} from 'projects/app-core/src/service/fileService';
import { lastValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { Currencies } from '../../../models/Currencies';
import { Tax } from '../../../models/Tax';
import { AnyoClubPass } from '../../../models/events/AnyoClubPasses';
import {
  EventEntryFee,
  EventPaymentType,
  ExternalSpeakers,
} from '../../../models/events/AnyoEvents';
import { EventBidsStatus } from '../../../models/events/EventsBiddingsCreateOrUpdateRequest';
import { CreateOrUpdateEventReqBody } from '../../../models/events/EventsCreateOrUpdateRequest';
import {
  EventBiddingLogListSort,
  EventsBiddingLogGridRequest,
} from '../../../models/events/eventBidsLogsGridRequest';
import { EventsBiddingLogResponse } from '../../../models/events/eventBidsLogsGridResponse';
import {
  EventsBidFilter,
  EventsBidsListsGridRequest,
} from '../../../models/events/eventsBidsGridRequest';
import { EventsBiddingListAdminResponse } from '../../../models/events/eventsBidsGridResponse';
import { PartnerTable } from '../../../models/partnerTable';
import { CirclesService } from '../../../services/circles.service';
import { EventsService } from '../../../services/events.service';
import { MasterdataService } from '../../../services/masterdata.service';
import { PartnerService } from '../../../services/partner.service';
import { AnyoSortDirection } from '../../lyfis/models/lyfis-questions-grid.models';

@Component({
  selector: 'app-crud-events',
  templateUrl: './crud-events.component.html',
  styleUrl: './crud-events.component.scss',
})
export class CrudEventsComponent extends AnyoComponent {
  loading: boolean = false;
  eventId: string = '';
  isEdit: boolean = false;
  viewMode = true;

  eventCategories: {
    label: string;
    value: string;
  }[] = [];

  shareImage!: File | null;
  images: File[] = [];
  shareMessage = '';
  eventEntryFee: EventEntryFee[] = [];

  presenterRows: {
    presenterId: string | null;
    isPaid: boolean;
    amount: number | null;
  }[] = [{ presenterId: null, isPaid: false, amount: null }];

  validateForm: FormGroup;
  dateTimeFormat = 'dd-MM-yyyy hh:mm a';
  dateFormat = 'dd-MM-yyyy';
  languages: string[] = [];
  eventPaymentTypes: string[] = Object.values(EventPaymentType);

  circleExpertsList: {
    expertId: string;
    name: string;
  }[] = [];

  guestSpeakerDetails: ExternalSpeakers[] = [];
  imagesLinks: string[] = [];
  shareImageLink: string = '';
  active: boolean = true;
  videoLink: string = '';
  meetLink: string = '';
  videoFile!: File;
  videoThumnailFile!: File;
  videoThumbnail = '';
  squareImage: File | undefined;
  squareImageLink: string | undefined;

  shareImagePreviewUrl: string | null = null;
  squareImagePreviewUrl: string | null = null;
  bannerImagesPreviewUrls: string[] = [];
  selectedBannerFiles: File[] = [];

  partners: PartnerTable[] = [];
  selectedPartners: string[] = [];
  corporateOnlyEvent: boolean = false;
  clubPasses: AnyoClubPass[] = [];
  testEvent: boolean = false;
  testRealPayment: boolean = false;
  isDiscussionAvailable: boolean = false;
  canUserPost: boolean = false;
  canExpertPost: boolean = false;

  recurring: boolean = false;
  cronText: string | undefined;
  rrText: string | undefined;
  recurringFrequencyText: string | undefined;
  offlineEvent: boolean = false;
  venue: string | undefined;
  selectedValue: string = 'Event';

  selectedBidId: string = '';
  bidsData: EventsBiddingListAdminResponse[] = [];
  isEventBid: boolean = false;

  constructor(
    auth: AuthService,
    private fb: NonNullableFormBuilder,
    private activatedRoute: ActivatedRoute,
    private masterData: MasterdataService,
    private eventsService: EventsService,
    private toasterService: ToastService,
    private circleService: CirclesService,
    private router: Router,
    private partnerService: PartnerService,
    private fileService: FileUtilsService,
  ) {
    super(auth);
    this.validateForm = fb.group({
      selectedCategory: ['', [Validators.required]],
      eventName: ['', [Validators.required]],
      eventDescription: ['', [Validators.required]],
      eventDuration: [0, [Validators.required]],
      maximumRegistrations: [0, [Validators.required]],
      keyPoints: [''],
      visibleTime: [new Date(), [Validators.required]],
      eventDate: [new Date(), [Validators.required]],
      eventEndDate: [''],
      registrationCutOff: [new Date(), [Validators.required]],
      selectedLanguages: [[], [Validators.required]],
      eventPaymentType: ['', [Validators.required]],
      applicableClubPasses: [[]],
    });

    this.validateForm
      .get('eventPaymentType')
      ?.valueChanges.subscribe((paymentType: string) => {
        if (paymentType === 'Free') {
          this.eventEntryFee = [];
        } else {
          if (this.eventEntryFee.length === 0) {
            this.addEntryFee();
          }
        }
      });

    this.validateForm.disable();
  }

  async ready(): Promise<void> {
    if (this.user) {
      this.pageLoading = true;
      const masterData = await lastValueFrom(this.masterData.masterDataList());
      this.languages = masterData.languages.map((value) => value.data);
      this.eventCategories = masterData.eventCategories.map((value) => {
        return {
          value: value.m_id,
          label: value.data,
        };
      });
      const circleMasterDataResponse = await lastValueFrom(
        this.circleService.getCircleMasterData(),
      );
      this.circleExpertsList = circleMasterDataResponse.circleExperts.map(
        (value) => {
          return {
            expertId: value.name,
            name: value._id,
          };
        },
      );
      await this.getClubPasses();
      await this.loadBidsData();

      const id = this.activatedRoute.snapshot.params['id'];
      this.eventId = id;
      this.partners = await this.partnerService.getAllActivePartners();

      if (id) {
        this.isEdit = true;
        this.viewMode = true;
        await this.getAndSetEvent(id);
      } else {
        this.isEdit = false;
        this.viewMode = false;
        this.validateForm.enable();
        const bidId = this.activatedRoute.snapshot.queryParams['bidId'];
        if (bidId) {
          await this.getAndSetTheEventsBidding(bidId);
          this.selectedBidId = bidId;
          this.isEventBid = true;
        }
      }
      this.pageLoading = false;
    }
  }

  addEntryFee() {
    const eventEntryFee: EventEntryFee = {
      id: '',
      amount: 0,
      currency: Currencies.INR,
      markupPrice: 0,
      tax: [],
    };
    this.eventEntryFee.push(eventEntryFee);
  }

  addPresenterRow() {
    this.presenterRows.push({
      presenterId: null,
      isPaid: false,
      amount: null,
    });
  }

  filteredExperts(index: number) {
    const selected = this.presenterRows
      .map((r) => r.presenterId)
      .filter((x) => x !== null);

    return this.circleExpertsList.filter(
      (expert) =>
        !selected.includes(expert.name) ||
        this.presenterRows[index].presenterId === expert.name,
    );
  }

  removeRow(index: number) {
    this.presenterRows.splice(index, 1);
  }

  onPresenterChange(index: number) {
    const row = this.presenterRows[index];
    row.isPaid = false;
    row.amount = null;
  }

  addGuestSpeaker() {
    this.guestSpeakerDetails.push({
      name: '',
      email: '',
      description: '',
      qualification: '',
      socialMediaLinks: {
        twitter: '',
        web: '',
        youtube: '',
        faceBook: '',
        linkedIn: '',
      },
    });
  }

  deleteGuestSpeaker(index: number) {
    this.guestSpeakerDetails.splice(index, 1);
  }

  deleteImage(index: number | undefined) {
    if (index != undefined) {
      this.imagesLinks.splice(index, 1);
    }
  }

  async getClubPasses() {
    this.eventsService.getAllClubPasses().subscribe({
      next: (value) => {
        this.clubPasses = value;
      },
      error: (error) => {
        const errorBody = error.error as IAnyoError;
        this.toasterService.showAnyoErrorToast(errorBody.description);
      },
    });
  }

  async loadBidsData(): Promise<void> {
    const req: EventsBidsListsGridRequest = {
      page: 1,
      pageSize: 50,
      filters: {} as Record<EventsBidFilter, string[]>,
      export: false,
    };
    req.filters!.EVENT_BID_STATUS = [EventBidsStatus.CLOSED];
    this.eventsService.getEventBidsListGrid(req).subscribe({
      next: (value) => {
        this.bidsData = value.data;
      },
      error: (error) => {
        const errorBody = error.error as IAnyoError;
        this.toasterService.showAnyoErrorToast(errorBody.description);
      },
    });
  }

  async getAndSetTheEventsBidding(id: string): Promise<void> {
    this.eventsService.getEventBidding(id).subscribe({
      next: async (value) => {
        this.selectedValue = value.isClass ? 'Class' : 'Event';
        this.validateForm.patchValue({
          eventName: value.eventName,
          eventDuration: value.duration,
          eventDate: value.eventDate,
        });
        const bidsLogData = await this.fetchLogs(id);
        this.presenterRows =
          (value.acceptedExpertIds ?? []).map((expertId) => {
            const log = bidsLogData.find((bid) => bid.expertId === expertId);
            return {
              presenterId: expertId,
              isPaid: log ? true : false,
              amount: log ? log.bidAmount : 0,
            };
          }) || [];
        this.offlineEvent = value.isOffline;
        this.testEvent = value.isTestEventBid;
      },
      error: (error) => {
        const errorBody = error.error as IAnyoError;
        this.toasterService.showAnyoErrorToast(errorBody.description);
      },
    });
  }

  async fetchLogs(eventBidId: string) {
    let bidsLogData: EventsBiddingLogResponse[] = [];
    const req: EventsBiddingLogGridRequest = {
      page: 1,
      pageSize: 50,
      filters: {
        EVENT_BID_ID: [eventBidId],
        EVENT_BID_LOG_STATUS: [],
      },
      sort: {} as Record<
        EventBiddingLogListSort,
        AnyoSortDirection | undefined
      >,
      export: true,
    };
    try {
      const response = await lastValueFrom(
        this.eventsService.getEventBidsLogGrid(req),
      );
      bidsLogData = response.data;
    } catch (err: any) {
      this.toasterService.showError(err.error.description);
    }
    return bidsLogData;
  }

  validateDateInFuture: ValidatorFn = (control: AbstractControl) => {
    if (!control.value) {
      return { required: true };
    }
    if (!moment(control.value as Date).isAfter(moment())) {
      return { error: true };
    }
    return {};
  };

  editForm() {
    this.viewMode = false;
    this.validateForm.enable();
  }

  mapToPaymentType(paymentType: string) {
    switch (paymentType) {
      case 'FREE':
        return 'Free';
      case 'PAID':
        return 'Paid';
      case 'CORPORATE_PAID':
        return 'Corporate Paid';
      case 'RETAIL_PAID':
        return 'Retail Paid';
      default:
        throw 'Invalid Event payment type passed';
    }
  }

  onClassEventChange(value: string): void {
    this.validateForm.get('eventPaymentType')?.setValue('');
    this.eventEntryFee = [];
    this.recurring = value === 'Class';
  }

  onDiscussionAvailableChange(): void {
    if (!this.isDiscussionAvailable) {
      this.canUserPost = false;
      this.canExpertPost = false;
    }
  }

  async handleMultipleFileUpload(files: File[]) {
    const uploadedFileUrls: string[] = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const uuid = uuidv4();
        const uuidWithoutHyphens = uuid.replace(/-/g, '');
        const extension = file.name.split('.').pop();
        const filePath = `events/${uuidWithoutHyphens}/images_.${extension}`;
        const uploadUrl = await lastValueFrom(
          this.fileService.generateUploadUrl(
            AnyoS3Buckets.CDN_BUCKET,
            filePath,
            file.type,
          ),
        );
        this.toasterService.showSuccess(`Uploading images ${i + 1}`);
        await lastValueFrom(this.fileService.uploadFile(file, uploadUrl.url));
        this.toasterService.showSuccess(`Uploading images ${i + 1} completed`);
        const fileUrl = `https://cdn.anyo.app/${filePath}`;
        uploadedFileUrls.push(fileUrl);
      }
      return uploadedFileUrls;
    } catch (e) {
      this.toasterService.showError(e + '');
      throw e;
    }
  }

  uploadImages($event: any) {
    const file = $event.target.files;
    if (file) {
      this.images = file;
      this.bannerImagesPreviewUrls.forEach((u) => URL.revokeObjectURL(u));
      this.bannerImagesPreviewUrls = [];
      const filesArray: File[] = Array.from(file as FileList);
      this.selectedBannerFiles = filesArray;
      for (const f of filesArray) {
        this.bannerImagesPreviewUrls.push(URL.createObjectURL(f));
      }
    }
  }

  deleteBannerPreview(index: number) {
    if (index >= 0 && index < this.bannerImagesPreviewUrls.length) {
      URL.revokeObjectURL(this.bannerImagesPreviewUrls[index]);
      this.bannerImagesPreviewUrls.splice(index, 1);
      if (this.selectedBannerFiles && this.selectedBannerFiles.length > index) {
        this.selectedBannerFiles.splice(index, 1);
      }
    }
  }

  uploadShareImage($event: any) {
    const file = $event.target.files[0];
    if (file) {
      this.shareImage = file;
      if (this.shareImagePreviewUrl) {
        URL.revokeObjectURL(this.shareImagePreviewUrl);
      }
      this.shareImagePreviewUrl = URL.createObjectURL(file);
    }
  }

  uploadThumbnailImage($event: any) {
    const file = $event.target.files[0];
    if (file) {
      this.squareImage = file;
      if (this.squareImagePreviewUrl) {
        URL.revokeObjectURL(this.squareImagePreviewUrl);
      }
      this.squareImagePreviewUrl = URL.createObjectURL(file);
    }
  }

  uploadThumbnail($event: any) {
    const file = $event.target.files[0];
    if (file) {
      this.videoThumnailFile = file;
    }
  }

  uploadVideo($event: any) {
    const file = $event.target.files[0];
    if (file) {
      this.videoFile = file;
    }
  }

  getFileNameFromUrl(url: string | undefined): string | null {
    if (!url) return null;
    try {
      const u = new URL(url);
      const name = u.pathname.split('/').pop();
      return name || null;
    } catch {
      const name = url.split('/').pop();
      return name || null;
    }
  }

  disableEndDate = (current: Date): boolean => {
    const eventDate = this.validateForm.get('eventDate')?.value;
    if (!eventDate) {
      return false;
    }
    const eventMoment = moment(eventDate);
    return current && !moment(current).isAfter(eventMoment, 'day');
  };

  async checkImagesRatio(
    files: File | File[],
    square: boolean,
  ): Promise<boolean> {
    const processFile = async (file: File): Promise<boolean> => {
      if (!(file instanceof File)) {
        return false;
      }
      const img = new Image();
      img.src = URL.createObjectURL(file);
      return new Promise<boolean>((resolve) => {
        img.onload = () => {
          const width = img.width;
          const height = img.height;
          const aspectRatio = width / height;
          if (square) {
            if (Math.abs(aspectRatio - 1) > 0.1) {
              this.toasterService.showError(
                `Image ${file.name} is not square. Please upload a square image.`,
              );
              resolve(false);
              return;
            }
          } else {
            if (aspectRatio < 1.9 || aspectRatio > 2.1) {
              this.toasterService.showError(
                `Image ${file.name} is not in a valid 2:1 rectangular ratio. Please upload an image with an aspect ratio between 1.90 and 2.10.`,
              );
              resolve(false);
              return;
            }
          }
          URL.revokeObjectURL(img.src);
          resolve(true);
        };
      });
    };

    if (Array.isArray(files)) {
      for (const file of files) {
        const isValid = await processFile(file);
        if (!isValid) {
          return false;
        }
      }
      return true;
    } else {
      return processFile(files);
    }
  }

  async getAndSetEvent(id: string): Promise<void> {
    this.eventsService.getEvents(id).subscribe({
      next: (value) => {
        this.active = value.active;
        this.offlineEvent = value.tags?.includes('OFFLINE_EVENT') || false;
        this.venue = value.venue;
        this.corporateOnlyEvent =
          (value.partners && value.partners.length > 0) || false;
        this.selectedPartners =
          value.partners?.map((partner) => partner.id) || [];
        this.recurring = value.tags
          ? value.tags.includes('RECURRING_EVENT')
          : false;
        this.selectedValue = this.recurring ? 'Class' : 'Event';
        this.rrText = value.rrRule;
        this.cronText = value.cronText;
        this.squareImageLink = value.squareImage;
        this.recurringFrequencyText = value.recurringText;
        this.selectedBidId = value.eventBidId || '';

        if (value.entryFee && value.entryFee.length) {
          this.eventEntryFee = value.entryFee;
        }

        this.meetLink = value.meetLink || '';
        this.videoLink = value.videoLink || '';
        this.videoThumbnail = value.videoThumbnail || '';
        this.imagesLinks = value.images || [];
        this.shareImageLink = value.shareImage || '';
        this.shareMessage = value.shareMessage || '';
        this.guestSpeakerDetails = value.externalSpeakers || [];

        this.isDiscussionAvailable =
          value.tags?.includes('IS_DISCUSSION_AVAILABLE') || false;
        this.canUserPost = value.tags?.includes('CAN_USER_POST') || false;
        this.canExpertPost = value.tags?.includes('CAN_EXPERT_POST') || false;

        const applicableClubPasses =
          value.applicableClubPasses
            ?.map((data) =>
              this.clubPasses.find((d) => d.passId === data.passId),
            )
            ?.filter((pass) => pass !== undefined) || [];

        this.validateForm.patchValue({
          selectedCategory: value.categoryId,
          eventName: value.name,
          eventDescription: value.description,
          eventDuration: value.durationMinutes,
          eventDate: value.eventDate,
          eventEndDate: value.eventEndDate,
          keyPoints: value.keyTakeAways,
          selectedLanguages: value.languages.split(','),
          eventPaymentType: this.mapToPaymentType(value.paymentType),
          registrationCutOff: value.registrationCutOffDate,
          visibleTime: value.visibleFrom,
          maximumRegistrations: value.maxRegistrations,
          applicableClubPasses,
        });

        this.presenterRows = [];
        const presenterIds = value.presenterDetailIds || [];
        const payoutMap = new Map<string, number>();

        if (value.payoutDetails && value.payoutDetails.length) {
          value.payoutDetails.forEach((p) => {
            payoutMap.set(p.presenterId, p.payout.amount);
          });
        }

        presenterIds.forEach((id) => {
          const paidAmount = payoutMap.get(id);
          this.presenterRows.push({
            presenterId: id,
            isPaid: paidAmount !== undefined,
            amount: paidAmount ?? null,
          });
        });

        if (this.presenterRows.length === 0) {
          this.presenterRows.push({
            presenterId: null,
            isPaid: false,
            amount: null,
          });
        }

        this.testEvent = value.tags?.includes('TEST_EVENT') || false;
        this.testRealPayment =
          value.tags?.includes('TEST_REAL_PAYMENT') || false;
        this.validateForm.disable();
      },
      error: (error) => {
        const errorBody = error.error as IAnyoError;
        this.toasterService.showAnyoErrorToast(
          errorBody.description || 'Failed to load event',
        );
      },
    });
  }

  async addOrUpdateEvent(): Promise<void> {
    if (!this.validateForm.valid) {
      Object.values(this.validateForm.controls).forEach((control) => {
        if (control.invalid) {
          this.toasterService.showError(
            'Check Any Field has got Error Message',
          );
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
      return;
    }

    const formValue = this.validateForm.value;

    if (
      this.eventEntryFee.some((value) => value.amount <= 0) &&
      formValue['eventPaymentType'] != 'Free'
    ) {
      this.toasterService.showAnyoErrorToast('Entry Fee should be greater 0');
      return;
    }

    const validPresenters = this.presenterRows.filter(
      (x) => x.presenterId !== null,
    );

    if (!validPresenters.length) {
      this.toasterService.showAnyoErrorToast(
        'Please select at least one Presenter',
      );
      return;
    }

    const invalidPayouts = this.presenterRows.filter(
      (x) => x.presenterId && x.isPaid && (!x.amount || x.amount <= 0),
    );

    if (invalidPayouts.length > 0) {
      this.toasterService.showAnyoErrorToast(
        'Please enter payout amount for presenters marked as paid',
      );
      return;
    }

    if (this.recurring) {
      if (!this.cronText) {
        this.toasterService.showAnyoErrorToast('Please enter cron text');
        return;
      }
      if (!this.rrText) {
        this.toasterService.showAnyoErrorToast('Please enter rr text');
        return;
      }
      const eventEndDateValue = this.validateForm.get('eventEndDate')?.value;
      if (!eventEndDateValue) {
        this.toasterService.showAnyoErrorToast('Please enter class end date');
        return;
      }
    }

    if (this.offlineEvent && !this.venue) {
      this.toasterService.showAnyoErrorToast(
        'Please enter venue for offline event',
      );
      return;
    }

    if (this.corporateOnlyEvent && !this.selectedPartners?.length) {
      this.toasterService.showAnyoErrorToast(
        'Please select at-least one partner for corporate event',
      );
      return;
    }

    const entryFee = this.eventEntryFee.map((value) => {
      const fee: EventEntryFee = {
        id: value.id,
        currency: Currencies.INR,
        markupPrice: value.markupPrice,
        amount: value.amount,
        tax: [] as Tax[],
      };
      return fee;
    });

    try {
      const validRows = this.presenterRows.filter((x) => x.presenterId);
      const presenterDetailIds = validRows.map((x) => x.presenterId!);
      const payoutDetails = validRows
        .filter((x) => x.isPaid)
        .map((x) => ({
          presenterId: x.presenterId!,
          payout: {
            currency: Currencies.INR,
            currencySymbol: '₹',
            amount: x.amount!,
          },
        }));

      const req: CreateOrUpdateEventReqBody = {
        active: this.active,
        categoryId: formValue['selectedCategory'] as string,
        description: formValue['eventDescription'] as string,
        durationMinutes: formValue['eventDuration'] as number,
        entryFee: entryFee,
        eventDate: moment(formValue['eventDate']).valueOf(),
        keyTakeAways: formValue['keyPoints'] as string,
        languages: (formValue['selectedLanguages'] as string[]).join(','),
        name: formValue['eventName'] as string,
        paymentType: formValue['eventPaymentType'] as string,
        presenterDetailIds: presenterDetailIds,
        registrationCutOffDate: this.recurring
          ? moment().endOf('day').valueOf()
          : moment(formValue['registrationCutOff']).endOf('day').valueOf(),
        visibleFrom: moment(formValue['visibleTime']).startOf('day').valueOf(),
        externalSpeakers: this.guestSpeakerDetails,
        images: this.imagesLinks,
        shareImage: this.shareImageLink,
        shareMessage: this.shareMessage,
        maxRegistrations: formValue['maximumRegistrations'] as number,
        partnerIds: this.selectedPartners,
        squareImage: this.squareImageLink,
        recurring: this.recurring,
        rrText: this.rrText,
        cronText: this.cronText,
        recurringText: this.recurringFrequencyText,
        offlineEvent: this.offlineEvent,
        venue: this.venue,
        meetLink: this.meetLink || undefined,
        videoLink: this.videoLink || undefined,
        videoThumbnail: this.videoThumbnail || undefined,
        isDiscussionAvailable: this.isDiscussionAvailable,
        canUserPost: this.canUserPost,
        canExpertPost: this.canExpertPost,
        applicableClubPasses:
          formValue['applicableClubPasses'].length > 0
            ? formValue['applicableClubPasses']
            : [],
      };

      if (!this.isEdit) {
        req.testEvent = this.testEvent;
        req.testRealPayment = this.testRealPayment;
      }

      if (formValue['eventEndDate'] && this.recurring) {
        req.eventEndDate = moment(formValue['eventEndDate']).valueOf();
      }

      if (this.selectedBidId) {
        req.eventBidId = this.selectedBidId;
      }

      if (payoutDetails.length > 0) {
        req.payoutDetails = payoutDetails;
      }

      this.loading = true;

      if (this.images && this.images.length) {
        const filesArray: File[] = Array.isArray(this.images)
          ? this.images
          : Array.from(this.images);
        if (!(await this.checkImagesRatio(filesArray, false))) {
          this.loading = false;
          return;
        }
        const imagesArray = await this.handleMultipleFileUpload(this.images);
        this.imagesLinks.push(...imagesArray);
        req.images = this.imagesLinks;
      }

      if (this.squareImage) {
        if (!(await this.checkImagesRatio(this.squareImage, true))) {
          this.loading = false;
          return;
        }
        const uuid = uuidv4();
        const extension = this.squareImage.name.split('.').pop();
        const uuidWithoutHyphens = uuid.replace(/-/g, '');
        const filePath = `events/${uuidWithoutHyphens}/squareImage_.${extension}`;
        const squareImageUploadUrl = await lastValueFrom(
          this.fileService.generateUploadUrl(
            AnyoS3Buckets.CDN_BUCKET,
            filePath,
            this.squareImage.type,
          ),
        );
        this.toasterService.showSuccess(`Uploading Square Image`);
        await lastValueFrom(
          this.fileService.uploadFile(
            this.squareImage,
            squareImageUploadUrl.url,
          ),
        );
        this.toasterService.showSuccess(`Uploading Square Image completed`);
        req.squareImage = `https://cdn.anyo.app/${filePath}`;
      }

      if (this.shareImage) {
        if (!(await this.checkImagesRatio(this.shareImage, false))) {
          this.loading = false;
          return;
        }
        const uuid = uuidv4();
        const uuidWithoutHyphens = uuid.replace(/-/g, '');
        const extension = this.shareImage.name.split('.').pop();
        const filePath = `events/${uuidWithoutHyphens}/shareimage_.${extension}`;
        const uploadUrl = await lastValueFrom(
          this.fileService.generateUploadUrl(
            AnyoS3Buckets.CDN_BUCKET,
            filePath,
            this.shareImage.type,
          ),
        );
        this.toasterService.showSuccess(`Uploading shareImage`);
        await lastValueFrom(
          this.fileService.uploadFile(this.shareImage, uploadUrl.url),
        );
        this.toasterService.showSuccess(`Uploading shareImage completed`);
        req.shareImage = `https://cdn.anyo.app/${filePath}`;
      }

      if (this.videoThumnailFile) {
        const uuid = uuidv4();
        const uuidWithoutHyphens = uuid.replace(/-/g, '');
        const extension = this.videoThumnailFile.name.split('.').pop();
        const filePath = `events/${uuidWithoutHyphens}/video_thumbnail.${extension}`;
        const uploadUrl = await lastValueFrom(
          this.fileService.generateUploadUrl(
            AnyoS3Buckets.CDN_BUCKET,
            filePath,
            this.videoThumnailFile.type,
          ),
        );
        this.toasterService.showSuccess(`Uploading Video Thumbnail`);
        await lastValueFrom(
          this.fileService.uploadFile(this.videoThumnailFile, uploadUrl.url),
        );
        this.toasterService.showSuccess(`Uploading Video Thumbnail completed`);
        req.videoThumbnail = `https://cdn.anyo.app/${filePath}`;
      }

      if (this.videoFile) {
        const uuid = uuidv4();
        const uuidWithoutHyphens = uuid.replace(/-/g, '');
        const extension = this.videoFile.name.split('.').pop();
        const filePath = `events/${uuidWithoutHyphens}/video_file.${extension}`;
        const uploadUrl = await lastValueFrom(
          this.fileService.generateUploadUrl(
            AnyoS3Buckets.CDN_BUCKET,
            filePath,
            this.videoFile.type,
          ),
        );
        this.toasterService.showSuccess(`Uploading video`);
        await lastValueFrom(
          this.fileService.uploadFile(this.videoFile, uploadUrl.url),
        );
        this.toasterService.showSuccess(`Uploading video completed`);
        req.videoLink = `https://cdn.anyo.app/${filePath}`;
      }

      if (this.isEdit) {
        req.eventId = this.eventId;
      }

      this.eventsService.addOrUpdateEvent(req).subscribe({
        next: () => {
          this.loading = false;
          this.toasterService.showSuccess(
            `Event ${this.isEdit ? 'Updated' : 'Created'} Successfully`,
          );
          this.router.navigate(['/events']);
        },
        error: (error) => {
          this.loading = false;
          const errorBody = error.error as IAnyoError;
          this.toasterService.showAnyoErrorToast(errorBody.description);
        },
      });
    } catch (error) {
      this.loading = false;
      this.toasterService.showError('An error occurred while saving the event');
    }
  }
}
